// 🌿 API Craft Back Office - Models Page
import React from 'react';
import { useModels } from '../hooks/useModels';
import { usePageTitle, PAGE_TITLES } from '../hooks/usePageTitle';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';

const ModelsPage: React.FC = () => {
  const { models, isLoading, error, refreshModels } = useModels();
  const navigate = useNavigate();
  
  // Set page title
  usePageTitle(PAGE_TITLES.MODELS);

  return (
    <Layout>
      <div className="space-y-8 animation-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-forest-800">Data Models</h1>
            <p className="mt-2 text-sage-700">
              Manage your dynamic data models and their structures
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={refreshModels}
              disabled={isLoading}
              className="btn-secondary disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Refreshing...
                </div>
              ) : (
                <>
                  <span className="mr-2">🔄</span>
                  Refresh
                </>
              )}
            </button>
            <Link
              to="/models/create"
              className="px-6 py-3 bg-gradient-to-r from-mint-500 to-emerald-600 text-white rounded-lg hover:from-mint-600 hover:to-emerald-700 transition-all shadow-soft flex items-center space-x-2"
            >
              <span>+</span>
              <span>Create Model</span>
            </Link>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="alert-error animation-scale-in">
            <span className="text-red-500 mr-3 text-xl">⚠️</span>
            <div>
              <h3 className="font-medium">Error loading models</h3>
              <p className="mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-gradient-to-r from-mint-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-strong mb-4 animation-bounce-gentle">
                <span className="text-2xl text-white">🌿</span>
              </div>
              <h3 className="text-lg font-semibold text-forest-800 mb-2">Loading Models</h3>
              <p className="text-sage-600">Please wait while we fetch your models...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && (!models || models.length === 0) && (
          <div className="text-center py-16 animation-scale-in">
            <div className="w-24 h-24 mx-auto bg-gradient-to-r from-sage-100 to-mint-100 rounded-2xl flex items-center justify-center mb-6 shadow-soft">
              <span className="text-4xl">📊</span>
            </div>
            <h3 className="text-2xl font-bold text-forest-800 mb-3">No models found</h3>
            <p className="text-sage-600 mb-8 max-w-md mx-auto">
              You haven't created any data models yet. Create your first model to start building dynamic APIs with API Craft.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/models/create" className="btn-primary">
                <span className="mr-2">✨</span>
                Create Your First Model
              </Link>
              <button className="btn-secondary">
                <span className="mr-2">📖</span>
                View Documentation
              </button>
            </div>
          </div>
        )}

        {/* Models Grid */}
        {!isLoading && !error && models && models.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 animation-slide-up">
            {models.map((model) => (
              <div 
                key={model.id} 
                className="card hover:shadow-strong transition-all duration-300 transform hover:scale-105 cursor-pointer bg-gradient-to-br from-white to-mint-50/30 border-2 border-transparent hover:border-mint-200"
                onClick={() => navigate(`/models/${model.model}`)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-mint-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-soft">
                      <span className="text-white text-xl">📋</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-forest-800">
                        {model.model}
                      </h3>
                      <p className="text-sm text-sage-600">
                        {model.table_structure.fields.length} fields
                      </p>
                    </div>
                  </div>
                  
                  <button className="btn-primary text-sm py-2 px-3 opacity-75 hover:opacity-100 transition-opacity">
                    Manage →
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <span className="font-medium text-sage-600">Table</span>
                    <p className="text-forest-800 font-mono text-xs break-all bg-sage-50 rounded px-2 py-1 mt-1">
                      {model.table_name}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-sage-600">Created</span>
                    <p className="text-forest-800 mt-1">
                      {new Date(model.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-sage-200">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-sage-500">
                      {model.table_structure.fields.length} fields
                    </span>
                    <span className="text-mint-600 font-medium">
                      Click to manage →
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ModelsPage;
